import { Novo } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Novo';
import { createFont, createTamagui } from '@tamagui/core';

import { animations } from './animations';
import { createTokens } from './createTokens';
import { media } from './media';

/**
 * HEADS UP! If any of the Novo token categories get renamed, make sure to
 * update documentation and consumers that reference token category names.
 */
const bricksNextTokens = createTokens({
  color: {
    ...Novo.color,
    transparent: 'transparent',
    ...Novo.shadow.shadowColor,
  },
  space: {
    ...Novo.space,
    ...Novo.sp,
    ...Novo.borderWidth,
  },
  radius: {
    ...Novo.radius,
    ...Novo.rad,
  },
  opacity: Novo.opacity,
  state: Novo.state,
  size: {
    ...Novo.size,
    ...Novo.sz,
  },
  zIndex: {},
  text: Novo.text,
  // shadow tokens
  elevation: Novo.shadow.elevation,
  shadowOffsetWidth: Novo.shadow.shadowOffsetWidth,
  shadowOffsetHeight: Novo.shadow.shadowOffsetHeight,
  shadowOpacity: Novo.shadow.shadowOpacity,
  shadowRadius: Novo.shadow.shadowRadius,
  percentages: Novo.percentage,
  backgroundBlur: Novo.backgroundBlur,
});

const graphik = createFont({
  // Have to add "true" for default font size, etc., because it's used by tamagui components like Dialog
  size: {
    ...Novo.typography.size,
    true: Novo.typography.size.bodyMedium,
  },
  lineHeight: {
    ...Novo.typography.lineHeight,
    true: Novo.typography.lineHeight.bodyMedium,
  },
  letterSpacing: {
    ...Novo.typography.letterSpacing,
    true: Novo.typography.letterSpacing.bodyMedium,
  },
  weight: {
    ...Novo.typography.weight,
    ...Novo.fontWeight,
    true: Novo.typography.weight.bodyMedium,
  },
  // TODO: Tokenize fallback font families
  family: `${Novo.typography.family}, system-ui, sans-serif`,
  // "face" is required for React Native to pick up different font weights. Can also add "italic" font names here later if needed
  // See: https://tamagui.dev/docs/core/configuration#font-tokens
  face: {
    400: { normal: 'Graphik-Regular' },
    500: { normal: 'Graphik-Medium' },
    600: { normal: 'Graphik-Semibold' },
    800: { normal: 'Graphik-Bold' },
  },
});

const bricksConfig = createTamagui({
  tokens: bricksNextTokens,
  fonts: {
    // Have to have at least one font called "body" to get some of Tamagui's types to work properly (FontSizeTokens, FontWeightTokens, etc.)
    body: graphik,
  },

  // Not using themes yet, but we can add them later. See: https://tamagui.dev/docs/intro/themes
  themes: { light: {} },

  // Defines breakpoints and other media queries. See: https://tamagui.dev/docs/core/use-media
  media,

  // See: https://tamagui.dev/docs/core/configuration#animations
  animations,

  // Only including shorthands for margin and padding for now. We may want to add more later.
  // Compare with tamagui's shorthands: https://github.com/tamagui/tamagui/blob/master/packages/shorthands/src/index.tsx
  shorthands: {
    m: 'margin',
    mb: 'marginBottom',
    ml: 'marginLeft',
    mr: 'marginRight',
    mt: 'marginTop',
    mx: 'marginHorizontal',
    my: 'marginVertical',
    p: 'padding',
    pb: 'paddingBottom',
    pl: 'paddingLeft',
    pr: 'paddingRight',
    pt: 'paddingTop',
    px: 'paddingHorizontal',
    py: 'paddingVertical',
  } as const,

  // Setting to false because we don't support dark mode yet
  shouldAddPrefersColorThemes: false,

  settings: {
    // Restricts what types are allowed for style prop values. Ideally these would all
    // be "strict". See: https://tamagui.dev/docs/core/configuration#settings
    allowedStyleValues: {
      // Allow tokens, %, numbers, and css values like auto/inherit
      space: 'somewhat-strict-web',

      // Allow tokens, %, numbers, and css values like auto/inherit
      size: 'somewhat-strict-web',

      // Only allow tokens
      radius: 'strict',

      // Allow tokens and numbers
      zIndex: 'somewhat-strict',

      // Only allow tokens
      color: 'strict',
    },

    // Don't autocomplete tokens like "$color.name" (just include "$name") for
    // space, size, radius, zIndex, color (prefixed tokens for custom categories
    // still included).  See: https://tamagui.dev/docs/core/configuration#settings
    autocompleteSpecificTokens: 'except-special',
  },

  // For other possible options, see: https://tamagui.dev/docs/core/configuration#others
});

type BricksConfig = typeof bricksConfig;

declare module '@tamagui/core' {
  // overrides TamaguiCustomConfig so our custom types
  // work everywhere you import `@tamagui/core`
  interface TamaguiCustomConfig extends BricksConfig {}

  interface TypeOverride {
    groupNames(): 'formLayout';
  }
}

export default bricksConfig;
